import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { NotificationSettingsTypes } from '@frontend/api-notification-settings';
import { WeavePopNotification } from '@frontend/types';

// for context on this list, see this google sheet: https://docs.google.com/spreadsheets/d/1szE3fFaBKbE84t3Ltcn6oy8SpXDOVx-obe-3ZvozJQA/edit#gid=0
export const allowedNotificationTypes: {
  id: NotificationSettingsTypes.NotificationSetting['id'];
  type: WeavePopNotification['type'];
  displayName: string;
  firestoreType: NotificationType;
  hideFromSettings?: boolean;
  name?: string;
  description?: string;
}[] = [
  {
    type: 'chat-message-new',
    /**
     * these id's are included here so that when a websocket message comes in,
     * we can check if the user has opted in to receive notifications for that type.
     * this is currently the only way to map a websocket message type to the user's notification settings.
     */
    id: '897b5b00-57cf-423e-b13e-646ab73880fb',
    displayName: 'Team Chat',
    firestoreType: NotificationType.CHAT_MESSAGE,
  },
  {
    type: 'fax-new',
    id: 'ec847541-be8c-4db2-97c0-b2fd4160334f',
    displayName: 'Fax',
    firestoreType: NotificationType.FAX,
  },
  {
    type: 'form-submission-new',
    id: 'f8f78f0f-f045-490e-b312-bf0facf45f1e',
    displayName: 'Forms',
    firestoreType: NotificationType.FORMS,
  },
  {
    type: 'review-new',
    id: '60ed9822-d248-4e0c-9ac5-99bff3346a64',
    displayName: 'Reviews',
    firestoreType: NotificationType.REVIEWS,
    hideFromSettings: true, // Messaging uses their own settings pages and routes
  },
  {
    type: 'sms-message-new',
    id: '46a812f4-aafc-4451-8267-faf61d14fd08',
    displayName: 'SMS Messages',
    firestoreType: NotificationType.SMS,
    hideFromSettings: true, // Messaging uses their own settings pages and routes
  },
  {
    type: 'payments',
    id: 'c60414e4-2b8a-46bd-ab73-c72b0834eb68',
    displayName: 'Payments',
    firestoreType: NotificationType.PAYMENTS,
  },
  {
    type: 'schedule-request-new',
    id: '3b0b62c3-9ac4-4022-8e7b-795fda0da62f',
    displayName: 'Schedule Request',
    firestoreType: NotificationType.SCHEDULE_REQUEST,
  },
  {
    type: 'voicemail-message',
    id: 'ed9eb6da-0243-4e14-8f00-d66a8e823b8e',
    displayName: 'Voicemail',
    firestoreType: NotificationType.VOICEMAIL_MESSAGE,
    name: 'Voicemail',
    description: 'Receive notifications for new voicemails',
  },
  {
    type: 'task-center',
    id: '5bd7d8cf-51bd-4478-bcee-30cd57f83015',
    displayName: 'Task Center',
    firestoreType: NotificationType.TASK,
  },
  {
    type: 'unhappy-call',
    id: '113739a1-8051-4b94-a805-1e7fd92ecadc',
    displayName: 'Call Intelligence',
    firestoreType: NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL,
  },
];

type PhoneNotificationType = (typeof allowedNotificationTypes)[0];

export const phoneNotificationTypes: PhoneNotificationType[] = [
  {
    type: 'device-connection',
    id: 'temp-id',
    displayName: 'Device Connection Changed',
    firestoreType: NotificationType.DEVICE_CONNECTION,
  },
  {
    type: 'incoming-call',
    id: 'e50f515c-34bd-4313-ac21-a87332fb30d9',
    displayName: 'Call Pop',
    firestoreType: NotificationType.PHONE_CALL_V2,
  },
  {
    type: 'missed-call',
    id: '03cd1035-1477-4d5c-af10-a26f85249921',
    displayName: 'Missed Call',
    firestoreType: NotificationType.MISSED_CALL,
    name: 'Missed Call',
    description: 'Receive missed call notifications',
  },
];

type TagNotificationType = (typeof allowedNotificationTypes)[0] & {
  name: string;
  description: string;
};

export const tagNotificationsType: TagNotificationType[] = [
  {
    id: '6928418b-0c1b-4b9b-ab1f-eee3d4c381e8',
    type: 'sms-tag',
    displayName: 'SMS Tags',
    firestoreType: NotificationType.SMS_TAG_V2,
    name: 'SMS Tags',
    description: 'Receive notifications when an SMS is tagged',
  },
  {
    id: '5dc1b862-b9d4-49b9-be86-4139a5359a29',
    type: 'call-record-tag',
    displayName: 'Call Record Tags',
    firestoreType: NotificationType.CALL_RECORD_TAG,
    name: 'Call Record Tags',
    description: 'Receive notifications when a call record is tagged',
  },
  {
    id: '85ef0a65-dec5-4f20-8e46-51a473fe454d',
    type: 'fax-tag',
    displayName: 'Fax Tags',
    firestoreType: NotificationType.FAX_TAG,
    name: 'Fax Tags',
    description: 'Receive notifications when a fax is tagged',
  },
  {
    id: 'b3290d60-4e1a-4391-a97d-2b8873225203',
    type: 'voicemail-tag',
    displayName: 'Voicemail Tags',
    firestoreType: NotificationType.VOICEMAIL_TAG,
    name: 'Voicemail Tags',
    description: 'Receive notifications when a voicemail is tagged',
  },
];

export const combinedTagNotificationType = {
  firestoreType: [
    NotificationType.SMS_TAG_V2,
    NotificationType.CALL_RECORD_TAG,
    NotificationType.VOICEMAIL_TAG,
    NotificationType.FAX_TAG,
  ],
  displayName: 'Tags',
  id: 'tags',
};

export const forYouNotificationTypes = [
  NotificationType.SMS_TAG_V2,
  NotificationType.CALL_RECORD_TAG,
  NotificationType.SMS,
  NotificationType.PAYMENTS,
];

// Add all the notification types here where you want subgroups
export const NotificationTypesWithSubGroups = [NotificationType.SMS];
export const allNotificationTypes = [...allowedNotificationTypes, ...phoneNotificationTypes, ...tagNotificationsType];
